import { useDialogs } from '../../components/ui'
import { useEffect, useState } from 'react'
import { adminApiService } from '../../services/adminApiService'
import { ApiError } from '../../services/BaseApiService'
import { BILLING_PLAN_NAMES, type AdminTeam, type DeferralSummary, type PagedResult } from '../../types'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../../styles/theme'
import AdminTabs from './AdminTabs'

// Mirrors BillingStatus server-side. Only the two that mean something is wrong are named here.
const BILLING_STATUS_OK = 10
const BILLING_STATUS_SUSPENDED = 30

function cents(n: number): string {
  return `$${(n / 100).toFixed(2)}`
}

/** The confirmation shown before assigning Free to a team that owes money. Exported for the test. */
export function freeDeferralMessage(teamName: string, preview: DeferralSummary): string {
  const lines = preview.items.map(i => {
    const what = i.kind === 'usage_charge' ? i.description
      : `${i.kind === 'failed_period' ? 'Unpaid bill' : 'Carried-forward balance'} for ${i.periodStart?.slice(0, 7)}`
    return `  • ${what}: ${cents(i.amountCents)}${i.inLaterPeriod ? ' (already in a later bill, not added again)' : ''}`
  })
  return [
    `Move "${teamName}" to Free?`,
    '',
    preview.items.length > 0 ? `These will be DEFERRED — not collected while on Free, billed on the first bill after the team returns to a paid plan (total ${cents(preview.totalCents)}):` : 'Nothing is owed.',
    ...lines,
    ...(preview.standingCleared ? ['', 'The team\'s past-due / suspended standing will be cleared (Free is never blocked).'] : []),
  ].join('\n')
}

export default function AdminTeamsPage() {
  const dialogs = useDialogs()
  const [result, setResult] = useState<PagedResult<AdminTeam> | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [busyTeam, setBusyTeam] = useState<string | null>(null)

  async function load(p: number) {
    setLoading(true)
    setError('')
    try {
      const data = await adminApiService.getTeams(p)
      setResult(data)
      setPage(p)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load teams')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load(1) }, [])

  async function setPlan(team: AdminTeam, planCode: string) {
    setBusyTeam(team.teamId)
    try {
      // Free is never charged: anything the team still owes is deferred (not collected, not written off) and billed
      // on its first bill after it returns to a paid plan. The admin sees exactly what before confirming.
      if (planCode === 'free') {
        const preview = await adminApiService.getFreeDeferralPreview(team.teamId)
        if (preview.items.length > 0 || preview.standingCleared) {
          const [title, ...rest] = freeDeferralMessage(team.name, preview).split('\n')
          if (!(await dialogs.confirm({ title, message: <div style={{ whiteSpace: 'pre-line' }}>{rest.join('\n').trim()}</div>, confirmLabel: 'Move to Free' }))) return
        }
      }
      await adminApiService.setTeamPlan(team.teamId, planCode)
      await load(page)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to change plan')
    } finally {
      setBusyTeam(null)
    }
  }

  async function toggleExempt(team: AdminTeam) {
    // A reason is required to grant an exemption and pointless to revoke one — the API enforces the
    // same rule, so cancelling here just avoids a round trip to be told so.
    let reason: string | undefined
    if (!team.billingExempt) {
      const entered = await dialogs.prompt({ title: `Exempt "${team.name}" from billing`, label: 'Reason', confirmLabel: 'Exempt', required: true })
      if (!entered?.trim()) return
      reason = entered.trim()
    }

    setBusyTeam(team.teamId)
    try {
      await adminApiService.setTeamBillingExempt(team.teamId, !team.billingExempt, reason)
      await load(page)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to change billing exemption')
    } finally {
      setBusyTeam(null)
    }
  }

  const teams = result?.items ?? []
  const total = result?.total ?? 0
  const pageSize = result?.pageSize ?? 20
  const totalPages = Math.ceil(total / pageSize)

  const thStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    textAlign: 'left',
    fontFamily: fontFamily.mono,
    fontSize: fontSize.xs,
    fontWeight: 400,
    color: colors.textMuted,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${colors.border}`,
    background: colors.background,
  }

  const tdStyle: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: fontSize.base,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
  }

  return (
    <>
    <div>
      <AdminTabs />

      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, marginBottom: spacing.xl }}>
        Teams
        {total > 0 && (
          <span style={{ fontSize: fontSize.base, fontWeight: 400, color: colors.textSecondary, marginLeft: spacing.sm }}>
            ({total} total)
          </span>
        )}
      </h1>

      {error && (
        <div style={{
          background: colors.errorLight, color: colors.error,
          border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm,
          padding: `${spacing.sm} ${spacing.md}`,
          fontSize: fontSize.sm, marginBottom: spacing.md,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: colors.textSecondary }}>Loading...</div>
      ) : teams.length === 0 ? (
        <div style={{ color: colors.textSecondary }}>No teams found.</div>
      ) : (
        <>
          <div style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: radius.md,
            boxShadow: shadow.sm,
            overflowX: 'auto',
            marginBottom: spacing.md,
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Team Name</th>
                  <th style={thStyle}>Team ID</th>
                  <th style={thStyle}>Users</th>
                  <th style={thStyle}>Plan</th>
                  <th style={thStyle}>Billing</th>
                  <th style={thStyle}>Created</th>
                </tr>
              </thead>
              <tbody>
                {teams.map(t => (
                  <tr
                    key={t.teamId}
                    onMouseEnter={() => setHoveredRow(t.teamId)}
                    onMouseLeave={() => setHoveredRow(null)}
                    style={{ background: hoveredRow === t.teamId ? colors.surfaceMist : 'transparent' }}
                  >
                    <td style={{ ...tdStyle, fontWeight: 500 }}>
                      {t.name}
                      {t.closedAt && (
                        <div data-testid="team-closed" style={{ fontSize: fontSize.xs, fontWeight: 400, color: colors.error, marginTop: 2 }}>
                          {t.erasedAt
                            ? `Closed ${new Date(t.closedAt).toLocaleDateString()} · erased ${new Date(t.erasedAt).toLocaleDateString()}`
                            : `Closed ${new Date(t.closedAt).toLocaleDateString()}${t.eraseAfter ? ` · erases after ${new Date(t.eraseAfter).toLocaleDateString()}` : ''}`}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontFamily: fontFamily.mono, fontSize: fontSize.sm, color: colors.textSecondary }}>
                      {t.teamId}
                    </td>
                    <td style={{ ...tdStyle, color: colors.textSecondary }}>
                      {t.userCount ?? '—'}
                    </td>
                    <td style={tdStyle}>
                      <select
                        value={t.planCode}
                        disabled={busyTeam === t.teamId}
                        onChange={e => setPlan(t, e.target.value)}
                        style={{
                          padding: '3px 6px', border: `1px solid ${colors.border}`,
                          borderRadius: radius.sm, background: colors.surface,
                          color: colors.text, fontSize: fontSize.sm,
                        }}
                      >
                        {/* Driven off the ladder, so a new plan appears here without an edit. */}
                        {Object.entries(BILLING_PLAN_NAMES).map(([code, name]) => (
                          <option key={code} value={code}>{name}</option>
                        ))}
                      </select>
                      {/* Shown on every plan including Ultimate: assigning a plan does not answer the
                          ask, because what they wanted was a rate nobody has published. */}
                      {t.customPricingRequestedAt && (
                        <span title={`Asked ${new Date(t.customPricingRequestedAt).toLocaleDateString()}`}
                              style={{ marginLeft: spacing.xs, fontSize: fontSize.xs, color: colors.warning }}>
                          · asked about pricing
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <button
                        type="button"
                        onClick={() => toggleExempt(t)}
                        disabled={busyTeam === t.teamId}
                        title={t.exemptReason ?? undefined}
                        style={{
                          padding: '3px 8px', borderRadius: radius.full, cursor: 'pointer',
                          fontSize: fontSize.xs,
                          border: `1px solid ${t.billingExempt ? colors.warning : colors.border}`,
                          background: t.billingExempt ? colors.warningLight : 'transparent',
                          color: t.billingExempt ? colors.warning : colors.textSecondary,
                        }}
                      >
                        {t.billingExempt ? 'Exempt' : 'Billed'}
                      </button>
                      {!t.billingExempt && t.billingStatus !== BILLING_STATUS_OK && (
                        <span style={{ marginLeft: spacing.xs, fontSize: fontSize.xs, color: colors.error }}>
                          {t.billingStatus === BILLING_STATUS_SUSPENDED ? 'suspended' : 'past due'}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: colors.textSecondary }}>
                      {new Date(t.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
              <button
                onClick={() => load(page - 1)}
                disabled={page <= 1}
                style={{
                  padding: '6px 12px',
                  background: 'none',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                  opacity: page <= 1 ? 0.5 : 1,
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: fontSize.sm, color: colors.textSecondary }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => load(page + 1)}
                disabled={page >= totalPages}
                style={{
                  padding: '6px 12px',
                  background: 'none',
                  border: `1px solid ${colors.border}`,
                  borderRadius: radius.sm,
                  fontSize: fontSize.sm,
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                  opacity: page >= totalPages ? 0.5 : 1,
                }}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
      {dialogs.element}
    </>
  )
}
